/**
 * TRUSTED DEVICE SERVICE
 * 
 * Gestiona dispositivos de confianza para evitar 2FA repetitivo.
 * 
 * FLUJO:
 * 1. Usuario hace login
 * 2. Si es dispositivo nuevo → Requiere 2FA (SMS)
 * 3. Si 2FA exitoso → Ofrece "Confiar en este dispositivo"
 * 4. Si confía → No pide 2FA por 30 días en este dispositivo
 * 
 * ALMACENAMIENTO:
 * - localStorage: fingerprint del dispositivo + fecha de confianza
 * - Firestore: lista de dispositivos de confianza del usuario
 * 
 * SEGURIDAD:
 * - Fingerprint único por navegador/dispositivo
 * - Máximo 5 dispositivos de confianza por usuario
 * - El usuario puede revocar dispositivos desde configuración
 */

import { auth, db } from '@/core/config/firebase';
import { Logger } from '@/core/utils';

const logger = Logger.getLogger('TrustedDeviceService');

// Duración de confianza: 30 días
const TRUST_DURATION_MS = 30 * 24 * 60 * 60 * 1000;

// Claves de localStorage
const DEVICE_ID_KEY = 'appmor_device_id';
const TRUST_KEY = 'appmor_trusted_device';

export interface TrustedDevice {
  deviceId: string;
  deviceName: string;
  browser: string;
  os: string;
  trustedAt: number;
  expiresAt: number;
  lastUsed: number;
}

export interface DeviceTrustStatus {
  isTrusted: boolean;
  deviceId: string;
  expiresAt: number | null;
  daysRemaining: number;
}

class TrustedDeviceService {
  private static instance: TrustedDeviceService;
  private deviceId: string | null = null;

  private constructor() {
    if (typeof window !== 'undefined') {
      this.deviceId = this.getOrCreateDeviceId();
    }
  }

  static getInstance(): TrustedDeviceService {
    if (!TrustedDeviceService.instance) {
      TrustedDeviceService.instance = new TrustedDeviceService();
    }
    return TrustedDeviceService.instance;
  }

  /**
   * Obtiene o crea un ID único para este dispositivo
   */
  private getOrCreateDeviceId(): string {
    let deviceId = localStorage.getItem(DEVICE_ID_KEY);
    
    if (!deviceId) {
      // Generar ID único
      deviceId = this.generateDeviceId();
      localStorage.setItem(DEVICE_ID_KEY, deviceId);
      logger.info('New device ID generated');
    }
    
    return deviceId;
  }

  /**
   * Genera un ID único basado en características del navegador
   */
  private generateDeviceId(): string {
    const components = [
      navigator.userAgent,
      navigator.language,
      screen.width + 'x' + screen.height,
      new Date().getTimezoneOffset().toString(),
      Array.from(crypto.getRandomValues(new Uint8Array(8)), b => b.toString(36)).join('').slice(0, 13)
    ];
    
    // Simple hash
    const str = components.join('|');
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
      const char = str.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash;
    }
    
    return 'dev_' + Math.abs(hash).toString(36) + '_' + Date.now().toString(36);
  }

  /**
   * Obtiene información del dispositivo actual
   */
  getDeviceInfo(): { name: string; browser: string; os: string } {
    const ua = navigator.userAgent;
    
    // Detectar OS
    let os = 'Desconocido';
    if (ua.includes('Windows')) os = 'Windows';
    else if (ua.includes('Mac')) os = 'macOS';
    else if (ua.includes('Linux')) os = 'Linux';
    else if (ua.includes('Android')) os = 'Android';
    else if (ua.includes('iPhone') || ua.includes('iPad')) os = 'iOS';
    
    // Detectar navegador
    let browser = 'Desconocido';
    if (ua.includes('Chrome') && !ua.includes('Edg')) browser = 'Chrome';
    else if (ua.includes('Safari') && !ua.includes('Chrome')) browser = 'Safari';
    else if (ua.includes('Firefox')) browser = 'Firefox';
    else if (ua.includes('Edg')) browser = 'Edge';
    
    return {
      name: `${browser} en ${os}`,
      browser,
      os
    };
  }

  /**
   * Verifica si el dispositivo actual es de confianza
   */
  async checkTrust(): Promise<DeviceTrustStatus> {
    const userId = auth.currentUser?.uid;
    
    if (!userId || !this.deviceId) {
      return {
        isTrusted: false,
        deviceId: this.deviceId || '',
        expiresAt: null,
        daysRemaining: 0
      };
    }

    try {
      // Primero verificar localStorage (más rápido)
      const localTrust = this.checkLocalTrust(userId);
      if (localTrust.isTrusted) {
        // Actualizar last used en Firestore (async, no esperar)
        this.updateLastUsed(userId).catch(() => {});
        return localTrust;
      }

      // Si no hay en local, verificar en Firestore
      const doc = await db.collection('trusted_devices')
        .doc(`${userId}_${this.deviceId}`)
        .get();

      if (doc.exists) {
        const data = doc.data() as TrustedDevice;
        
        if (data.expiresAt > Date.now()) {
          // Guardar en localStorage para próxima vez
          this.saveLocalTrust(userId, data.expiresAt);
          
          return {
            isTrusted: true,
            deviceId: this.deviceId,
            expiresAt: data.expiresAt,
            daysRemaining: Math.ceil((data.expiresAt - Date.now()) / (24 * 60 * 60 * 1000))
          };
        } else {
          // Expirado, eliminar
          await doc.ref.delete();
          this.clearLocalTrust();
        }
      }

      return {
        isTrusted: false,
        deviceId: this.deviceId,
        expiresAt: null,
        daysRemaining: 0
      };
    } catch (error) {
      logger.error('Error checking device trust', error);
      return {
        isTrusted: false,
        deviceId: this.deviceId || '',
        expiresAt: null,
        daysRemaining: 0
      };
    }
  }

  /**
   * Verifica confianza local (localStorage)
   */
  private checkLocalTrust(userId: string): DeviceTrustStatus {
    try {
      const stored = localStorage.getItem(TRUST_KEY);
      if (!stored) {
        return { isTrusted: false, deviceId: this.deviceId || '', expiresAt: null, daysRemaining: 0 };
      }

      const data = JSON.parse(stored);
      
      if (data.userId === userId && 
          data.deviceId === this.deviceId && 
          data.expiresAt > Date.now()) {
        return {
          isTrusted: true,
          deviceId: this.deviceId || '',
          expiresAt: data.expiresAt,
          daysRemaining: Math.ceil((data.expiresAt - Date.now()) / (24 * 60 * 60 * 1000))
        };
      }
    } catch (e) {
      // Ignorar errores
    }

    return { isTrusted: false, deviceId: this.deviceId || '', expiresAt: null, daysRemaining: 0 };
  }

  /**
   * Guarda confianza local
   */
  private saveLocalTrust(userId: string, expiresAt: number): void {
    try {
      localStorage.setItem(TRUST_KEY, JSON.stringify({
        userId,
        deviceId: this.deviceId,
        expiresAt
      }));
    } catch (e) {
      // Ignorar
    }
  }

  /**
   * Limpia confianza local
   */
  private clearLocalTrust(): void {
    try {
      localStorage.removeItem(TRUST_KEY);
    } catch (e) {
      // Ignorar
    }
  }

  /**
   * Registra este dispositivo como de confianza
   */
  async trustDevice(): Promise<boolean> {
    const userId = auth.currentUser?.uid;
    
    if (!userId || !this.deviceId) {
      logger.error('Cannot trust device: no user or device ID');
      return false;
    }

    try {
      const deviceInfo = this.getDeviceInfo();
      const now = Date.now();
      const expiresAt = now + TRUST_DURATION_MS;

      const trustedDevice: TrustedDevice = {
        deviceId: this.deviceId,
        deviceName: deviceInfo.name,
        browser: deviceInfo.browser,
        os: deviceInfo.os,
        trustedAt: now,
        expiresAt,
        lastUsed: now
      };

      // Guardar en Firestore
      await db.collection('trusted_devices')
        .doc(`${userId}_${this.deviceId}`)
        .set(trustedDevice);

      // Guardar en localStorage
      this.saveLocalTrust(userId, expiresAt);

      logger.info('Device trusted successfully', { deviceId: this.deviceId });
      return true;
    } catch (error) {
      logger.error('Failed to trust device', error);
      return false;
    }
  }

  /**
   * Actualiza la última vez que se usó el dispositivo
   */
  private async updateLastUsed(userId: string): Promise<void> {
    if (!this.deviceId) return;
    
    try {
      await db.collection('trusted_devices')
        .doc(`${userId}_${this.deviceId}`)
        .update({ lastUsed: Date.now() });
    } catch (e) {
      // Ignorar errores
    }
  }

  /**
   * Revoca la confianza de este dispositivo
   */
  async revokeDevice(): Promise<boolean> {
    const userId = auth.currentUser?.uid;
    
    if (!userId || !this.deviceId) return false;

    try {
      await db.collection('trusted_devices')
        .doc(`${userId}_${this.deviceId}`)
        .delete();
      
      this.clearLocalTrust();
      
      logger.info('Device trust revoked', { deviceId: this.deviceId });
      return true;
    } catch (error) {
      logger.error('Failed to revoke device', error);
      return false;
    }
  }

  /**
   * Obtiene todos los dispositivos de confianza del usuario
   */
  async getUserDevices(): Promise<TrustedDevice[]> {
    const userId = auth.currentUser?.uid;
    if (!userId) return [];

    try {
      const snapshot = await db.collection('trusted_devices')
        .where('expiresAt', '>', Date.now())
        .get();

      // Filtrar solo los del usuario actual (el ID del doc empieza con el userId)
      return snapshot.docs
        .filter(doc => doc.id.startsWith(userId + '_'))
        .map(doc => doc.data() as TrustedDevice);
    } catch (error) {
      logger.error('Failed to get user devices', error);
      return [];
    }
  }

  /**
   * Revoca un dispositivo específico por su ID
   */
  async revokeDeviceById(deviceId: string): Promise<boolean> {
    const userId = auth.currentUser?.uid;
    if (!userId) return false;

    try {
      await db.collection('trusted_devices')
        .doc(`${userId}_${deviceId}`)
        .delete();
      
      // Si es el dispositivo actual, limpiar localStorage
      if (deviceId === this.deviceId) {
        this.clearLocalTrust();
      }
      
      logger.info('Device revoked', { deviceId });
      return true;
    } catch (error) {
      logger.error('Failed to revoke device', error);
      return false;
    }
  }

  /**
   * Revoca todos los dispositivos del usuario
   */
  async revokeAllDevices(): Promise<boolean> {
    const userId = auth.currentUser?.uid;
    if (!userId) return false;

    try {
      const devices = await this.getUserDevices();
      
      await Promise.all(
        devices.map(d => 
          db.collection('trusted_devices')
            .doc(`${userId}_${d.deviceId}`)
            .delete()
        )
      );
      
      this.clearLocalTrust();
      
      logger.info('All devices revoked');
      return true;
    } catch (error) {
      logger.error('Failed to revoke all devices', error);
      return false;
    }
  }

  /**
   * Verifica si el dispositivo actual es de confianza (shorthand for checkTrust)
   */
  async isDeviceTrusted(): Promise<boolean> {
    const status = await this.checkTrust();
    return status.isTrusted;
  }

  /**
   * Obtiene el ID del dispositivo actual
   */
  getCurrentDeviceId(): string | null {
    return this.deviceId;
  }
}

export const trustedDeviceService = TrustedDeviceService.getInstance();





